import { ChangeEvent, FormEvent, useState } from 'react';
import {
  Button,
  TextField,
  Paper,
  Box,
  Typography,
  CssBaseline,
  Container,
  Link as MuiLink,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useLogin } from '@/lib/api/auth';
import { PublicOnlyGuard } from '@/features/auth/PublicOnlyGuard.tsx';
import useSnackbar from '@/hooks/useSnackbar';
import { useAnalytics } from '@/hooks/useAnalytics';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const { mutateAsync: login } = useLogin();
  const { show: showSnackbar } = useSnackbar();
  const { trackEvent, identifyUser } = useAnalytics();

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (email.trim() === '' || password.trim() === '') {
      showSnackbar({ title: 'Email y contraseña son requeridos', variant: 'error' });
      trackEvent('login_validation_error', { reason: 'missing_credentials' });
      return;
    }

    try {
      trackEvent('login_submitted', { method: 'password' });
      const loginResult = await login({ email, password });

      if (!loginResult.emailValidated) {
        showSnackbar({
          title: 'El email no está validado, revisa tu bandeja de entrada.',
          variant: 'info',
        });
        trackEvent('login_failed', { reason: 'email_not_validated' });
        return;
      } else if (loginResult.active) {
        localStorage.setItem('token', loginResult.token);
        identifyUser(loginResult.id);
        trackEvent('login_success', { method: 'password' });
        navigate('/');
      } else {
        showSnackbar({
          title: 'El usuario no tiene la cuota al día, por favor ponte en contacto con nosotros.',
          variant: 'error',
        });
        trackEvent('login_failed', { reason: 'inactive_subscription' });
      }
    } catch {
      showSnackbar({
        title: 'Usuario o contraseña incorrectos',
        variant: 'error',
      });
      trackEvent('login_failed', { reason: 'invalid_credentials' });
    }
  };

  return (
    <PublicOnlyGuard>
      <CssBaseline />
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: { xs: 2, sm: 4 },
            width: '100%',
            boxShadow: { xs: 'none', sm: 6 },
            background: { xs: 'transparent', sm: 'background.paper' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ mb: { xs: 3, sm: 2 } }}>
              <img
                src="/images/logos/inversorhub-logo.png"
                alt="InversorHUB Logo"
                style={{
                  maxWidth: '200px',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
              Acceso
            </Typography>
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 1, position: 'relative', width: '100%' }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Dirección de email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={handleEmailChange}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Contraseña"
                type="password"
                id="password"
                value={password}
                onChange={handlePasswordChange}
                autoComplete="current-password"
              />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                Iniciar sesión
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <MuiLink
                  component={Link}
                  to="/recuerda"
                  sx={{
                    display: 'block',
                    mb: 1.5,
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  ¿No recuerdas la contraseña? Recupérala
                </MuiLink>
                <MuiLink
                  component={Link}
                  to="/altas"
                  sx={{
                    display: 'block',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  ¿No tienes cuenta? Créala aquí
                </MuiLink>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </PublicOnlyGuard>
  );
}
